import React, { useState, useEffect, useRef } from "react";
import {
  ArrowLeft,
  Search,
  Calendar,
  Package2,
  User,
  ChevronDown,
  ChevronUp,
  History,
  Printer,
  CreditCard,
  Banknote,
  X,
  XCircle,
} from "lucide-react";
import BaseApi from "../../services/base-api";
import Price from "../../components/Currency/Price";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import ContactInfo from "../../components/ContactInfo";
import LoadingComponent from "../../components/LoadingComponent";
import { LocalizedText } from "../../types/LocalizedText";
import { useToast } from "../../context/ToastContext";
import i18n from "../../i18/i18n";
import { useDebounce } from "../../hooks/useDebounce";

interface Customer {
  id: string;
  firstName: string;
  lastName: string;
  phone: string;
  email: string;
  address: string;
}

interface Address {
  name: string;
  type: string;
  phone: string;
}

interface ProductSnapshot {
  name: LocalizedText;
  price: string;
  images: string[];
}

interface Product {
  id: string;
  sku: string;
  name: LocalizedText;
  images: string[];
}

interface OrderItem {
  id: string;
  orderId: string;
  productId: string;
  variationId: string | null;
  productName: LocalizedText;
  productSku: string;
  size: string;
  color: string;
  variationDetails: any;
  quantity: number;
  unitPrice: string;
  subtotal: string;
  discount: string;
  total: string;
  productSnapshot: ProductSnapshot;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  product: Product;
}

interface Sale {
  id: string;
  orderNumber: string;
  userId: string;
  status: "delivered" | "pending" | "cancelled";
  paymentStatus: "paid" | "pending" | "failed";
  paymentMethod: string;
  paymentDetails: Record<string, any>;
  remarks?: string | null;
  subtotal: string;
  tax: string;
  taxRate: string;
  discount: string;
  discountCode: string | null;
  shippingFee: string;
  total: string;
  currency: string;
  shippingAddress: Address;
  billingAddress: Address;
  customerNotes: string;
  internalNotes: string | null;
  trackingNumber: string | null;
  shippingCarrier: string | null;
  estimatedDeliveryDate: string | null;
  deliveredAt: string;
  isGift: boolean;
  giftMessage: string | null;
  recipientInfo: any;
  deliveryOTP: string | null;
  deliveryOTPVerified: boolean;
  assignedDriverId: string | null;
  source: string;
  ipAddress: string;
  userAgent: string;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  customer: Customer;
  items: OrderItem[];
  invoice?: {
    id: string;
    invoiceNumber?: string;
  } | null;
}

interface Pagination {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  itemsPerPage: number;
  hasNextPage: boolean;
  hasPrevPage: boolean;
}

const POSHistory: React.FC = () => {
  const [sales, setSales] = useState<Sale[]>([]);
  const [initialLoading, setInitialLoading] = useState<boolean>(true);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const debouncedSearchTerm = useDebounce(searchTerm.trim());
  const latestRequestRef = useRef(0);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pagination, setPagination] = useState<Pagination | null>(null);
  const [invoiceLoading, setInvoiceLoading] = useState<string | null>(null);
  const [settleSale, setSettleSale] = useState<Sale | null>(null);
  const [settlePaymentMethod, setSettlePaymentMethod] = useState<
    "cash" | "card"
  >("cash");
  const [settleRemarks, setSettleRemarks] = useState("");
  const [settleLoading, setSettleLoading] = useState(false);
  const [cancelSale, setCancelSale] = useState<Sale | null>(null);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelLoading, setCancelLoading] = useState(false);
  const { t } = useTranslation();
  const { success, err } = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    fetchSales(currentPage);
  }, [currentPage]);

  const fetchSales = async (page: number = 1): Promise<void> => {
    const requestId = ++latestRequestRef.current;
    try {
      setLoading(true);
      const response = await BaseApi.get(`/pos/sales?page=${page}`);
      if (requestId !== latestRequestRef.current) return;

      if (response.success && response.data) {
        setSales(response.data);
        setPagination(response.pagination);
      } else {
        setError(t("Failed to load sales history"));
      }
    } catch (err) {
      if (requestId !== latestRequestRef.current) return;
      setError(t("Error fetching sales data"));
      console.error(err);
    } finally {
      if (requestId === latestRequestRef.current) {
        setLoading(false);
        setInitialLoading(false);
      }
    }
  };

  const searchLower = debouncedSearchTerm.toLowerCase();
  const filteredSales = sales.filter(
    (sale) =>
      sale.orderNumber?.toLowerCase().includes(searchLower) ||
      sale.customer?.firstName?.toLowerCase().includes(searchLower) ||
      sale.customer?.lastName?.toLowerCase().includes(searchLower),
  );

  const toggleExpand = (id: string): void => {
    setExpandedId(expandedId === id ? null : id);
  };

  const handleGenerateInvoice = async (orderId: string) => {
    try {
      setInvoiceLoading(orderId);
      const res = await BaseApi.post(`/invoices/generate/${orderId}`, {
        language: i18n.language,
        autoSend: true,
      });

      if (!res.success) {
        throw new Error("Failed to generate invoice");
      }

      const generatedInvoice = res.data?.invoice || res.data;
      if (!generatedInvoice?.id) {
        throw new Error("Invoice generated but ID was not returned");
      }

      setSales((prev) =>
        prev.map((sale) =>
          sale.id === orderId
            ? {
                ...sale,
                invoice: {
                  id: generatedInvoice.id,
                  invoiceNumber: generatedInvoice.invoiceNumber,
                },
              }
            : sale,
        ),
      );
      success(t("Invoice generated successfully"));
    } catch (error) {
      console.error("Error generating invoice:", error);
      err(t("Failed to generate invoice"));
    } finally {
      setInvoiceLoading(null);
    }
  };

  const handleDownloadInvoice = async (invoiceId: string) => {
    window.open(
      `${process.env.REACT_APP_API_BASE_URL}/invoices/${invoiceId}/download`,
      "_blank",
    );
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getStatusColor = (status: string): string => {
    const colors: Record<string, string> = {
      delivered: "bg-green-100 text-green-800",
      pending: "bg-yellow-100 text-yellow-800",
      cancelled: "bg-red-100 text-red-800",
    };
    return colors[status] || "bg-gray-100 text-gray-800";
  };

  const getPaymentStatusColor = (status: string): string => {
    const colors: Record<string, string> = {
      paid: "bg-green-100 text-green-800",
      pending: "bg-yellow-100 text-yellow-800",
      failed: "bg-red-100 text-red-800",
    };
    return colors[status] || "bg-gray-100 text-gray-800";
  };

  const shouldShowSettlePayment = (sale: Sale): boolean =>
    String(sale.paymentMethod || "").toLowerCase() === "credit" &&
    String(sale.paymentStatus || "").toLowerCase() === "pending";

  const openSettleModal = (sale: Sale) => {
    setSettleSale(sale);
    setSettlePaymentMethod("cash");
    setSettleRemarks("");
  };

  const closeSettleModal = () => {
    if (settleLoading) return;
    setSettleSale(null);
    setSettlePaymentMethod("cash");
    setSettleRemarks("");
  };

  const handleSettlePayment = async () => {
    if (!settleSale) return;

    try {
      setSettleLoading(true);
      const response = await BaseApi.patch(`/pos/sales/${settleSale.id}/settle`, {
        paymentMethod: settlePaymentMethod,
        remarks: settleRemarks.trim() || undefined,
      });

      if (!response?.success) {
        throw new Error(response?.message || "Failed to settle payment");
      }

      setSales((prev) =>
        prev.map((sale) =>
          sale.id === settleSale.id
            ? {
                ...sale,
                ...((response?.data as Partial<Sale>) || {}),
                paymentMethod:
                  response?.data?.paymentMethod || settlePaymentMethod,
                paymentStatus: response?.data?.paymentStatus || "paid",
              }
            : sale,
        ),
      );

      success(t("Payment settled successfully"));
      closeSettleModal();
    } catch (error: any) {
      console.error("Error settling POS payment:", error);
      err(error?.message || t("Failed to settle payment"));
    } finally {
      setSettleLoading(false);
    }
  };

  const openCancelModal = (sale: Sale) => {
    setCancelSale(sale);
    setCancelReason("");
  };

  const closeCancelModal = () => {
    if (cancelLoading) return;
    setCancelSale(null);
    setCancelReason("");
  };

  const handleCancelSale = async () => {
    if (!cancelSale || !cancelReason) {
      err(t("Please provide a cancellation reason"));
      return;
    }

    try {
      setCancelLoading(true);
      const response = await BaseApi.patch(`/orders/${cancelSale.id}/status`, {
        status: "cancelled",
        cancellationReason: cancelReason,
        cancelledAt: new Date().toISOString(),
      });

      if (!response?.success) {
        throw new Error(
          response?.message || t("Failed to update order status. Please try again!"),
        );
      }

      setSales((prev) =>
        prev.map((sale) =>
          sale.id === cancelSale.id
            ? {
                ...sale,
                ...((response?.data as Partial<Sale>) || {}),
                status: "cancelled",
              }
            : sale,
        ),
      );
      success(t("Order status updated successfully"));
      closeCancelModal();
    } catch (error: any) {
      console.error("Error cancelling POS sale:", error);
      err(
        error?.message || t("Failed to update order status. Please try again!"),
      );
    } finally {
      setCancelLoading(false);
    }
  };

  if (initialLoading) {
    return <LoadingComponent title={t("Point of Sales")} />;
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 p-6 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 text-lg">{error}</p>
          <button
            onClick={() => fetchSales(currentPage)}
            className="mt-4 px-4 py-2 bg-[#0d223f] text-white rounded-lg hover:bg-[#0d223f]"
          >
            {t("Retry")}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f2e6db] py-8 px-4">
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div
          className="bg-white rounded-2xl shadow-xl p-4 sm:p-6 mb-4 sm:mb-6"
          style={{ animation: "fadeIn 0.5s ease-out" }}
        >
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
            <div className="flex items-center gap-3 sm:gap-4">
              <div className="bg-gradient-to-br from-gray-700 to-black rounded-2xl p-4">
                <History className="w-6 h-6 sm:w-8 sm:h-8 text-white" />
              </div>
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold text-gray-800">
                  {t("Sales History")}
                </h1>
                <p className="text-sm sm:text-base text-gray-600">
                  {t("View and manage all POS transactions")}
                </p>
                {/* Back Button */}
                <button
                  onClick={() => navigate("/admin/pos")}
                  className="flex items-center gap-2 text-gray-700 hover:text-black transition-colors"
                >
                  <ArrowLeft className="w-5 h-5" />
                  <span className="font-semibold">Back to Point of Sale</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Search Bar */}
        <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder={t("Search by order number or customer name...")}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>

        {/* Sales List */}
        <div
          className={
            "space-y-4" +
            (loading ? " opacity-50 pointer-events-none transition-opacity" : "")
          }
        >
          {filteredSales.length === 0 ? (
            <div className="bg-white rounded-lg shadow-sm p-8 text-center">
              <Package2 className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600 text-lg">{t("No sales found")}</p>
            </div>
          ) : (
            filteredSales.map((sale) => (
              <div
                key={sale.id}
                className="bg-white rounded-lg shadow-sm overflow-hidden"
              >
                {/* Sale Header */}
                <div
                  className="p-4 cursor-pointer hover:bg-gray-50 transition-colors"
                  onClick={() => toggleExpand(sale.id)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-lg font-semibold text-gray-900">
                          {sale.orderNumber}
                        </h3>
                        <span
                          className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(
                            sale.status,
                          )}`}
                        >
                          <span className="capitalize">{sale.status}</span>
                        </span>
                        <span
                          className={`px-2 py-1 rounded-full text-xs font-medium ${getPaymentStatusColor(
                            sale.paymentStatus,
                          )}`}
                        >
                          <span className="capitalize">
                            {sale.paymentStatus}
                          </span>
                        </span>
                      </div>

                      <div className="flex flex-wrap gap-4 text-sm text-gray-600">
                        <div className="flex items-center font-semibold">
                          <User className="w-5 h-5 mr-2" />
                          {sale.customer?.firstName} {sale.customer?.lastName}
                        </div>
                        <div className="flex items-center">
                          {sale.customer?.email && (
                            <ContactInfo
                              type="email"
                              value={sale.customer?.email}
                            />
                          )}
                        </div>
                        <div className="flex items-center">
                          {sale.customer?.phone && (
                            <ContactInfo
                              type="phone"
                              value={sale.customer?.phone}
                            />
                          )}
                        </div>
                        <div className="flex items-center">
                          <Calendar className="w-5 h-5 mr-1" />
                          {formatDate(sale.createdAt)}
                        </div>
                        <div className="flex items-center">
                          <Package2 className="w-5 h-5 mr-1" />
                          {sale.items?.length || 0} {t("item(s)")}
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-4">
                      {shouldShowSettlePayment(sale) && (
                        <button
                          onClick={(event) => {
                            event.stopPropagation();
                            openSettleModal(sale);
                          }}
                          className="inline-flex items-center gap-2 rounded-lg bg-black px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-gray-800"
                        >
                          <CreditCard className="h-4 w-4" />
                          {t("Make Payment")}
                        </button>
                      )}
                      <div className="text-right">
                        <div
                          className={`flex items-center text-2xl font-bold text-gray-900 ${sale.paymentStatus === "paid" ? "text-green-700" : "text-gray-500"}`}
                        >
                          <Price amount={Number(sale.total)} />
                        </div>
                        <div className="text-xs text-gray-500 uppercase">
                          {t(
                            sale.paymentMethod === "maagoud_gift"
                              ? "Maagoud Gift"
                              : sale.paymentMethod === "send_link"
                                ? "Send Link"
                                : sale.paymentMethod === "credit"
                                  ? "Credit"
                                  : sale.paymentMethod === "card"
                                    ? "Card"
                                    : "Cash",
                          )}
                        </div>
                      </div>
                      {expandedId === sale.id ? (
                        <ChevronUp className="w-5 h-5 text-gray-400" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-gray-400" />
                      )}
                    </div>
                  </div>
                </div>

                {/* Expanded Details */}
                {expandedId === sale.id && (
                  <div className="border-t border-gray-600 p-4 bg-gray-50">
                    <div className="flex justify-end no-print gap-2">
                      {sale.status !== "cancelled" && (
                        <button
                          onClick={() => openCancelModal(sale)}
                          className="inline-flex items-center gap-2 px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm font-medium"
                        >
                          <XCircle className="w-4 h-4" />
                          {t("Cancel Order")}
                        </button>
                      )}
                      {!sale.invoice?.id ? (
                        <button
                          onClick={() => handleGenerateInvoice(sale.id)}
                          disabled={invoiceLoading === sale.id}
                          className="inline-flex items-center gap-2 px-3 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors text-sm font-medium disabled:opacity-50"
                        >
                          {invoiceLoading === sale.id ? (
                            <div
                              className="w-4 h-4 border-2 border-white border-t-transparent rounded-full"
                              style={{ animation: "spin 1s linear infinite" }}
                            />
                          ) : (
                            <Printer className="w-4 h-4" />
                          )}
                          {invoiceLoading === sale.id
                            ? t("Generating...")
                            : t("Generate Invoice")}
                        </button>
                      ) : (
                        <button
                          onClick={() => handleDownloadInvoice(sale.invoice!.id)}
                          disabled={invoiceLoading === sale.invoice?.id}
                          className="inline-flex items-center gap-2 px-3 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors text-sm font-medium disabled:opacity-50"
                        >
                          {invoiceLoading === sale.invoice?.id ? (
                            <div
                              className="w-4 h-4 border-2 border-white border-t-transparent rounded-full"
                              style={{ animation: "spin 1s linear infinite" }}
                            />
                          ) : (
                            <Printer className="w-4 h-4" />
                          )}
                          {t("Print Invoice")}
                        </button>
                      )}
                    </div>

                    <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-4 mb-2">
                      <div className="space-y-1">
                        <h4 className="font-semibold text-gray-900 mb-2">
                          {t("Customer Details")}
                        </h4>
                        <p className="text-sm text-gray-600">
                          <span className="font-medium">{t("Name")}: </span>{" "}
                          {sale.shippingAddress?.name}
                        </p>
                        <p className="text-sm text-gray-600">
                          <span className="font-medium">{t("Phone")}: </span>{" "}
                          {sale.customer?.phone || "N/A"}
                        </p>
                        <p className="text-sm text-gray-600">
                          <span className="font-medium">{t("Email")}: </span>{" "}
                          {sale.customer?.email || "N/A"}
                        </p>
                        <p className="text-sm text-gray-600">
                          <span className="font-medium">{t("Address")}: </span>{" "}
                          {sale.customer?.address || "N/A"}
                        </p>
                      </div>

                      <div className="space-y-1">
                        <h4 className="font-semibold text-gray-900 mb-2">
                          {t("Payment Details")}
                        </h4>
                        <p className="text-sm text-gray-600">
                          <span className="font-medium mr-1">
                            {"Subtotal"}:{" "}
                          </span>
                          <Price amount={Number(sale.subtotal)} />
                        </p>
                        <p className="text-sm text-gray-600">
                          <span className="font-medium">
                            {"Tax"} ({sale.taxRate}%):{" "}
                          </span>
                          <Price amount={Number(sale.tax)} />
                        </p>
                        <p className="text-sm text-gray-600">
                          <span className="font-medium">{t("Discount")}: </span>
                          <Price amount={Number(sale.discount)} />
                        </p>
                        <p className="text-sm text-gray-900 font-semibold mt-1 mr-1">
                          <span className="font-medium">{t("Total")}: </span>
                          <Price amount={Number(sale.total)} />
                        </p>
                        {(sale.remarks || sale.paymentDetails?.remarks) && (
                          <p className="text-sm text-gray-600 mt-2">
                            <span className="font-medium">{t("Remarks")}: </span>
                            {sale.remarks || sale.paymentDetails?.remarks}
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="p-4">
                      <h4 className="font-semibold text-gray-900 mb-2">
                        {t("Product Details")}
                      </h4>
                      <div className="space-y-2">
                        {sale.items?.map((item) => (
                          <div
                            key={item.id}
                            className="flex justify-between items-center bg-white p-3 rounded-lg"
                          >
                            {item.product?.images?.length && (
                              <img
                                src={item.product?.images[0]}
                                alt="product"
                                className="h-16 w-16 object-contain"
                              />
                            )}
                            <div className="flex-1">
                              <p className="font-semibold text-gray-900">
                                {(i18n.language === "ar"
                                  ? item.productName?.ar
                                  : item.productName?.en) || t("Product")}
                              </p>
                              <p className="text-sm text-gray-600">
                                {t("SKU")}: {item.productSku} | {t("Qty")}:{" "}
                                {item.quantity}
                              </p>
                            </div>

                            <div className="flex-1">
                              {item?.size && (
                                <p className="font-semibold text-gray-900">
                                  {t("Size")}: {item.size}
                                </p>
                              )}
                              {item?.color && (
                                <p className="text-sm text-gray-600">
                                  {t("Color")}: {item.color}
                                </p>
                              )}
                            </div>

                            <div className="text-right">
                              <p className="font-semibold text-gray-900">
                                <Price amount={Number(item.total)} />
                              </p>
                              <p className="text-xs text-gray-600">
                                <Price amount={Number(item.unitPrice)} />{" "}
                                {t("each")}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))
          )}
        </div>

        {/* Pagination */}
        {pagination && pagination.totalPages > 1 && (
          <div className="mt-6 flex items-center justify-center gap-2">
            <button
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={!pagination.hasPrevPage}
              className="px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t("Previous")}
            </button>
            <span className="px-4 py-2 text-gray-700">
              {t("Page")} {pagination.currentPage} of {pagination.totalPages}
            </span>
            <button
              onClick={() => setCurrentPage((p) => p + 1)}
              disabled={!pagination.hasNextPage}
              className="px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t("Next")}
            </button>
          </div>
        )}
      </div>

      {cancelSale && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl">
            <div className="mb-6 flex items-start justify-between gap-4">
              <div>
                <h3 className="text-2xl font-bold text-gray-900">
                  {t("Cancel Order")}
                </h3>
                <p className="mt-2 text-sm text-gray-600">
                  {t(
                    "Please provide a reason for cancelling this order. This action cannot be undone.",
                  )}
                </p>
                <p className="mt-2 text-sm font-semibold text-gray-800">
                  {cancelSale.orderNumber}
                </p>
              </div>
              <button
                onClick={closeCancelModal}
                disabled={cancelLoading}
                className="rounded-lg p-2 text-gray-500 transition hover:bg-gray-100 hover:text-gray-700 disabled:cursor-not-allowed"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <label className="block text-sm font-semibold text-gray-700">
              {t("Cancellation Reason")} <span className="text-red-500">*</span>
              <select
                value={cancelReason}
                onChange={(event) => setCancelReason(event.target.value)}
                disabled={cancelLoading}
                className="mt-2 w-full rounded-lg border-2 border-gray-300 px-4 py-3 font-normal focus:border-red-500 focus:outline-none disabled:cursor-not-allowed disabled:bg-gray-100"
              >
                <option value="">{t("Select a reason")}</option>
                <option value="customer_request">{t("Customer Request")}</option>
                <option value="out_of_stock">{t("Out of Stock")}</option>
                <option value="payment_failed">{t("Payment Failed")}</option>
                <option value="fraudulent">{t("Fraudulent Order")}</option>
                <option value="address_issue">{t("Address Issue")}</option>
                <option value="other">{t("Other")}</option>
              </select>
            </label>

            <div className="mt-6 flex justify-end gap-3">
              <button
                onClick={closeCancelModal}
                disabled={cancelLoading}
                className="rounded-lg border border-gray-300 px-4 py-2 font-medium text-gray-700 transition hover:bg-gray-50 disabled:cursor-not-allowed"
              >
                {t("Cancel")}
              </button>
              <button
                onClick={handleCancelSale}
                disabled={cancelLoading || !cancelReason}
                className="inline-flex items-center gap-2 rounded-lg bg-red-600 px-4 py-2 font-medium text-white transition hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {cancelLoading && (
                  <span
                    className="h-4 w-4 rounded-full border-2 border-white border-t-transparent"
                    style={{ animation: "spin 1s linear infinite" }}
                  />
                )}
                {cancelLoading ? t("Cancelling...") : t("Cancel Order")}
              </button>
            </div>
          </div>
        </div>
      )}

      {settleSale && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl">
            <div className="mb-6 flex items-start justify-between gap-4">
              <div>
                <h3 className="text-2xl font-bold text-gray-900">
                  {t("Make Payment")}
                </h3>
                <p className="mt-2 text-sm text-gray-600">
                  {t("Select how to settle this credit sale.")}
                </p>
                <p className="mt-2 text-sm font-semibold text-gray-800">
                  {settleSale.orderNumber}
                </p>
              </div>
              <button
                onClick={closeSettleModal}
                disabled={settleLoading}
                className="rounded-lg p-2 text-gray-500 transition hover:bg-gray-100 hover:text-gray-700 disabled:cursor-not-allowed"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => setSettlePaymentMethod("cash")}
                className={`flex flex-col items-center gap-2 rounded-xl border-2 p-4 transition-all ${
                  settlePaymentMethod === "cash"
                    ? "border-black bg-gray-50"
                    : "border-gray-200 hover:border-gray-300"
                }`}
              >
                <Banknote className="h-8 w-8" />
                <span className="font-semibold">{t("Cash")}</span>
              </button>
              <button
                onClick={() => setSettlePaymentMethod("card")}
                className={`flex flex-col items-center gap-2 rounded-xl border-2 p-4 transition-all ${
                  settlePaymentMethod === "card"
                    ? "border-black bg-gray-50"
                    : "border-gray-200 hover:border-gray-300"
                }`}
              >
                <CreditCard className="h-8 w-8" />
                <span className="font-semibold">{t("Card")}</span>
              </button>
            </div>

            <div className="mt-5">
              <label className="block text-sm font-semibold text-gray-700">
                {t("Remarks")} <span className="text-gray-500">({t("Optional")})</span>
                <textarea
                  value={settleRemarks}
                  onChange={(event) => setSettleRemarks(event.target.value)}
                  disabled={settleLoading}
                  rows={3}
                  placeholder={t("Enter remarks")}
                  className="mt-2 w-full resize-none rounded-lg border-2 border-gray-300 px-4 py-3 font-normal focus:border-black focus:outline-none disabled:cursor-not-allowed disabled:bg-gray-100"
                />
              </label>
            </div>

            <div className="mt-6 flex justify-end gap-3">
              <button
                onClick={closeSettleModal}
                disabled={settleLoading}
                className="rounded-lg border border-gray-300 px-4 py-2 font-medium text-gray-700 transition hover:bg-gray-50 disabled:cursor-not-allowed"
              >
                {t("Cancel")}
              </button>
              <button
                onClick={handleSettlePayment}
                disabled={settleLoading}
                className="inline-flex items-center gap-2 rounded-lg bg-black px-4 py-2 font-medium text-white transition hover:bg-gray-800 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {settleLoading ? (
                  <>
                    <div
                      className="h-4 w-4 rounded-full border-2 border-white border-t-transparent"
                      style={{ animation: "spin 1s linear infinite" }}
                    />
                    {t("Processing...")}
                  </>
                ) : (
                  t("Confirm Payment")
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default POSHistory;
